// Normalization + formatting + config helpers for the Soundstats artist
// dashboard. Pure functions + localStorage cache, no React. Theme-agnostic —
// the chart palette here leans green to match the Lyve shell.

export const COMBINED_KEY = '__all__';

// Green-forward palette (used for country bars, genre donut, fallback platform colors).
export const PALETTE = [
  '#00dc49', '#00dc49', '#00dc49', '#00dc49',
  '#6AA8FF', '#FFB020', '#FF5252', '#22d3ee',
];

// Brand colors per platform — chips, compare bars, single-platform lines.
export const PLATFORM_COLORS = {
  [COMBINED_KEY]: '#00dc49',
  spotify: '#1DB954',
  youtube: '#FF0000',
  'youtube-music': '#FF3E3E',
  instagram: '#E1306C',
  facebook: '#1877F2',
  tiktok: '#FE2C55',
  x: '#E7E9EA',
  soundcloud: '#FF5500',
  deezer: '#A238FF',
  bandsintown: '#1B7BD3',
  songkick: '#F8000C',
  'apple-music': '#FA243C',
  amazon: '#25D1DA',
  tidal: '#00E5FF',
  shazam: '#0066FF',
  pandora: '#36A0F9',
  napster: '#1F8FE5',
  boomplay: '#FF6B00',
  netease: '#E60012',
  'qq-music': '#31C27C',
  itunes: '#FB5BC5',
  billboard: '#FFB400',
  google: '#4285F4',
  twitch: '#9146FF',
};

const FALLBACK_PLATFORM_COLORS = ['#00dc49', '#6AA8FF', '#00dc49', '#FFB020', '#FF5252', '#22d3ee'];

export function platformColor(key) {
  if (PLATFORM_COLORS[key]) return PLATFORM_COLORS[key];
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return FALLBACK_PLATFORM_COLORS[h % FALLBACK_PLATFORM_COLORS.length];
}

export function shade(hex, amt) {
  const c = String(hex).replace('#', '');
  if (c.length !== 6) return hex;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const m = v => Math.max(0, Math.min(255, Math.round(v + v * amt)));
  const to = v => m(v).toString(16).padStart(2, '0');
  return '#' + to(r) + to(g) + to(b);
}

const PLATFORM_NAMES = {
  x: 'X (Twitter)', 'youtube-music': 'YouTube Music', 'apple-music': 'Apple Music',
  'qq-music': 'QQ Music', soundcloud: 'SoundCloud', tiktok: 'TikTok',
  bandsintown: 'Bandsintown', songkick: 'Songkick', deezer: 'Deezer',
  spotify: 'Spotify', instagram: 'Instagram', facebook: 'Facebook',
  youtube: 'YouTube', pandora: 'Pandora', shazam: 'Shazam',
  napster: 'Napster', tidal: 'Tidal', boomplay: 'Boomplay',
  netease: 'NetEase', amazon: 'Amazon', itunes: 'iTunes',
  billboard: 'Billboard', google: 'Google', twitch: 'Twitch',
};

export const CATEGORIES = [
  { id: 'core', label: 'Overview' },
  { id: 'audience', label: 'Audience' },
  { id: 'social', label: 'Social' },
  { id: 'listen', label: 'Listeners' },
  { id: 'stream', label: 'Streams' },
  { id: 'popularity', label: 'Popularity' },
  { id: 'retention', label: 'Retention' },
  { id: 'report', label: 'Report' },
  { id: 'shorts', label: 'Shorts' },
  { id: 'songRanks', label: 'Song Charts' },
  { id: 'albumRanks', label: 'Album Charts' },
  { id: 'playlistCurrent', label: 'Playlists' },
  { id: 'playlistReach', label: 'Playlist Reach' },
];

export const SERIES_GROUPS = {
  audience: {
    title: 'Audience', compare: 'followerCount',
    metrics: [
      { key: 'followerCount', label: 'Followers' },
      { key: 'viewCount', label: 'Views' },
      { key: 'likeCount', label: 'Likes' },
      { key: 'postCount', label: 'Posts' },
    ],
  },
  social: { title: 'Social', compare: 'value', metrics: [{ key: 'value', label: 'Engagement' }] },
  listen: { title: 'Listeners', compare: 'value', metrics: [{ key: 'value', label: 'Listeners' }] },
  stream: { title: 'Streams', compare: 'value', metrics: [{ key: 'value', label: 'Streams' }] },
  popularity: { title: 'Popularity', compare: 'value', metrics: [{ key: 'value', label: 'Popularity' }] },
  retention: {
    title: 'Retention', compare: 'conversionRate',
    metrics: [
      { key: 'conversionRate', label: 'Conversion %' },
      { key: 'followers', label: 'Followers' },
      { key: 'listeners', label: 'Listeners' },
    ],
  },
  playlistReach: {
    title: 'Playlist Reach', compare: 'playlistReach',
    metrics: [
      { key: 'playlistReach', label: 'Reach' },
      { key: 'playlistCount', label: 'Playlists' },
      { key: 'playlistEditorialReach', label: 'Editorial reach' },
      { key: 'playlistUserReach', label: 'User reach' },
    ],
  },
};

export function fmtNum(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US');
}

export function compact(n) {
  if (n == null || isNaN(n)) return '0';
  const a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (a >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(Math.round(n));
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtDuration(sec) {
  if (sec == null || isNaN(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m + ':' + (s < 10 ? '0' + s : s);
}

export function fmtMetric(key, v) {
  if (v == null || v === '') return '—';
  if (key === 'conversionRate') return Number(v).toFixed(1);
  return fmtNum(v);
}

export function titleCase(s) {
  if (!s) return '';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function platformLabel(k) {
  if (k === COMBINED_KEY) return 'All platforms';
  return PLATFORM_NAMES[k] || titleCase((k || '').replace(/-/g, ' '));
}

export function initial(name) {
  return name ? name.charAt(0).toUpperCase() : '?';
}

export function stripHtml(s) {
  if (s == null) return '';
  return String(s).replace(/<[^>]*>/g, '').replace(/\s*\r?\n\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

export function tagLabel(x) {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  if (typeof x === 'object' && x.name) return x.name;
  return String(x);
}

function asArray(v) {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.items)) return v.items;
  return null;
}

function seriesValue(item) {
  if (item == null) return null;
  if (typeof item.value === 'number') return item.value;
  const keys = ['followerCount', 'viewCount', 'likeCount', 'postCount', 'listeners', 'plays',
    'popularity', 'conversionRate', 'followers', 'reach', 'count', 'monthlyListeners', 'monthly_listeners', 'subscriberCount'];
  for (let i = 0; i < keys.length; i++) {
    if (typeof item[keys[i]] === 'number') return item[keys[i]];
  }
  return null;
}

function itemDate(item) {
  if (!item) return null;
  return item.date || item.timestamp || item.positionDate || item.entryDate || item.rankDate || null;
}

export function sortAsc(items) {
  return items.slice().sort((a, b) => new Date(a.date || a.positionDate || 0) - new Date(b.date || b.positionDate || 0));
}

export function isRatioKey(key) {
  return key === 'conversionRate';
}

export function parseScore(rawScore) {
  const items = asArray(rawScore);
  if (!items || !items.length) return null;
  const hasSC = items.some(i => i.scScore != null || i.fanbaseScore != null || i.trendingScore != null);
  let out = [];
  if (hasSC) {
    items.forEach(it => {
      const d = itemDate(it);
      if (!d) return;
      const sc = it.scScore != null ? it.scScore : it.score != null ? it.score : null;
      if (sc == null && it.fanbaseScore == null && it.trendingScore == null) return;
      out.push({ date: d, scScore: sc, fanbaseScore: it.fanbaseScore, trendingScore: it.trendingScore });
    });
  } else {
    const buckets = {};
    items.forEach(it => {
      const d = itemDate(it);
      if (!d) return;
      const v = seriesValue(it);
      if (v == null) return;
      const p = String(it.platform || '').toLowerCase();
      const key = p.indexOf('fanbase') >= 0 ? 'fanbaseScore' : p.indexOf('trending') >= 0 ? 'trendingScore' : 'scScore';
      if (!buckets[d]) buckets[d] = {};
      buckets[d][key] = v;
    });
    out = Object.keys(buckets).sort().map(d => ({
      date: d,
      scScore: buckets[d].scScore,
      fanbaseScore: buckets[d].fanbaseScore,
      trendingScore: buckets[d].trendingScore,
    }));
  }
  out.sort((a, b) => new Date(a.date) - new Date(b.date));
  return out.length ? out : null;
}

export function parseStats(rawStats) {
  if (!rawStats || typeof rawStats !== 'object' || Array.isArray(rawStats)) return null;
  const toMap = arr => {
    const out = {};
    if (!Array.isArray(arr)) return out;
    arr.forEach(it => {
      if (!it || typeof it.platform !== 'string') return;
      if (typeof it.value !== 'number') return;
      out[it.platform] = it.value;
    });
    return out;
  };
  const out = {
    social: toMap(rawStats.social),
    streaming: toMap(rawStats.streaming),
    popularity: toMap(rawStats.popularity),
    retention: toMap(rawStats.retention),
  };
  const any = ['social', 'streaming', 'popularity', 'retention'].some(k => Object.keys(out[k]).length > 0);
  return any ? out : null;
}

export function buildNormalized(json) {
  const data = json.data || json.core || {};
  const errors = json.errors || {};
  const N = { uuid: json.uuid };
  const AVAIL = {};
  const hasErr = key => !!(errors[key] && String(errors[key]).trim());
  const hasErrors = Object.keys(errors).some(k => hasErr(k));

  const metaRaw = data.meta && data.meta.object ? data.meta.object : data.meta;
  N.meta = metaRaw && (metaRaw.name || metaRaw.slug) ? metaRaw : null;
  AVAIL.meta = !!(N.meta && !hasErr('meta'));

  N.score = parseScore(data.score) || [];
  AVAIL.score = N.score.length > 0 && !hasErr('score');

  N.stats = parseStats(data.stats);
  AVAIL.stats = !!(N.stats && !hasErr('stats'));
  AVAIL.statsSocial = AVAIL.stats && Object.keys(N.stats.social || {}).length > 0;
  AVAIL.statsStreaming = AVAIL.stats && Object.keys(N.stats.streaming || {}).length > 0;
  AVAIL.statsPopularity = AVAIL.stats && Object.keys(N.stats.popularity || {}).length > 0;
  AVAIL.statsRetention = AVAIL.stats && Object.keys(N.stats.retention || {}).length > 0;

  N.albums = asArray(data.albums) || [];
  AVAIL.albums = N.albums.length > 0 && !hasErr('albums');
  N.songs = asArray(data.songs) || [];
  AVAIL.songs = N.songs.length > 0 && !hasErr('songs');
  N.identifiers = asArray(data.identifiers) || [];
  AVAIL.identifiers = N.identifiers.length > 0 && !hasErr('identifiers');
  N.events = asArray(data.events) || [];
  AVAIL.events = N.events.length > 0 && !hasErr('events');
  N.related = asArray(data.related) || [];
  AVAIL.related = N.related.length > 0 && !hasErr('related');
  N.broadcasts = asArray(data.broadcasts) || [];
  AVAIL.broadcasts = N.broadcasts.length > 0 && !hasErr('broadcasts');
  N.broadcastGroups = asArray(data.broadcastGroups) || [];
  AVAIL.broadcastGroups = N.broadcastGroups.length > 0 && !hasErr('broadcastGroups');

  return { N, AVAIL, hasErrors };
}

export function topPlatforms(map) {
  const arr = [];
  Object.keys(map || {}).forEach(p => {
    const v = map[p];
    if (v != null && !isNaN(v)) arr.push({ label: platformLabel(p), value: v });
  });
  arr.sort((a, b) => b.value - a.value);
  return arr.slice(0, 10);
}

export function groupHasErrors(json) {
  const errors = json && json.errors;
  if (!errors) return false;
  return Object.keys(errors).some(k => errors[k] && String(errors[k]).trim());
}

export function platformEntries(json) {
  const out = [];
  const d = json.data || {};
  const errors = json.errors || {};
  for (const k in d) {
    if (!Object.prototype.hasOwnProperty.call(d, k)) continue;
    const v = d[k];
    if (v == null || typeof v === 'string') continue;
    if (errors[k] && String(errors[k]).trim()) continue;
    const items = v.items;
    if (!Array.isArray(items) || !items.length) continue;
    out.push({ key: k, items, page: v.page, related: v.related });
  }
  return out;
}

export function reportPlatforms(json) {
  const out = [];
  const d = json.data || {};
  const errors = json.errors || {};
  for (const k in d) {
    if (!Object.prototype.hasOwnProperty.call(d, k)) continue;
    const v = d[k];
    if (!v || typeof v !== 'object') continue;
    if (errors[k] && String(errors[k]).trim()) continue;
    if (!v.object) continue;
    out.push({ key: k, object: v.object });
  }
  return out;
}

export function withCombined(entries) {
  return [{ key: COMBINED_KEY, all: true }].concat(entries);
}

export function combinedSeries(platforms, metrics) {
  const dateSet = {};
  const byMetricDate = {};
  metrics.forEach(m => {
    byMetricDate[m.key] = {};
    platforms.forEach(e => {
      (e.items || []).forEach(it => {
        const d = it.date || it.positionDate;
        if (!d || it[m.key] == null) return;
        if (!byMetricDate[m.key][d]) byMetricDate[m.key][d] = { sum: 0, count: 0 };
        byMetricDate[m.key][d].sum += it[m.key];
        byMetricDate[m.key][d].count++;
        dateSet[d] = true;
      });
    });
  });
  const dates = Object.keys(dateSet).sort((a, b) => new Date(a) - new Date(b));
  const datasets = metrics.map((m, i) => ({
    label: m.label,
    data: dates.map(d => {
      const cell = byMetricDate[m.key][d];
      if (!cell) return null;
      return isRatioKey(m.key) ? cell.sum / cell.count : cell.sum;
    }),
    color: PALETTE[i % PALETTE.length],
  }));
  return { dates, datasets };
}

export function sumLatest(platforms, metric) {
  let total = 0;
  let n = 0;
  platforms.forEach(e => {
    const items = sortAsc(e.items || []);
    const v = items.length ? items[items.length - 1][metric] : null;
    if (v != null) { total += v; n++; }
  });
  return { total, n };
}

export function combinedChangePct(platforms, metric) {
  let nowSum = 0;
  let prevSum = 0;
  platforms.forEach(e => {
    const items = sortAsc(e.items || []);
    if (items.length >= 1) nowSum += items[items.length - 1][metric] || 0;
    if (items.length >= 2) prevSum += items[items.length - 2][metric] || 0;
  });
  if (prevSum <= 0) return null;
  return ((nowSum - prevSum) / prevSum) * 100;
}

export function sumCountries(platforms) {
  const m = {};
  platforms.forEach(e => {
    const items = sortAsc(e.items || []);
    let latest = null;
    for (let i = 0; i < items.length; i++) if (items[i].countryPlots && items[i].countryPlots.length) latest = items[i];
    if (!latest) return;
    latest.countryPlots.forEach(c => {
      const code = c.countryCode || c.countryName;
      if (!m[code]) m[code] = { name: c.countryName || code, value: 0 };
      m[code].value += c.value || 0;
    });
  });
  const arr = Object.keys(m).map(k => m[k]);
  arr.sort((a, b) => b.value - a.value);
  return arr.slice(0, 10);
}

export function topCountries(items) {
  let latest = null;
  for (let i = 0; i < items.length; i++) {
    if (items[i].countryPlots && items[i].countryPlots.length) latest = items[i];
  }
  if (!latest) return null;
  const m = {};
  for (let j = 0; j < latest.countryPlots.length; j++) {
    const c = latest.countryPlots[j];
    const code = c.countryCode || c.countryName;
    if (!m[code]) m[code] = { name: c.countryName || code, value: 0 };
    m[code].value += c.value || 0;
  }
  const arr = Object.keys(m).map(k => m[k]);
  arr.sort((a, b) => b.value - a.value);
  return arr.slice(0, 10);
}

export function trendPct(items, key) {
  if (!items || items.length < 2) return null;
  const latest = items[items.length - 1][key];
  const prev = items[items.length - 2][key];
  if (latest == null || prev == null || prev === 0) return null;
  return ((latest - prev) / prev) * 100;
}

// ── 24-hour localStorage cache (keyed ss.cache.{uuid}.{group}) ──
const CACHE_PREFIX = 'ss.cache.';
const CACHE_TTL = 24 * 60 * 60 * 1000;

function cacheKey(uuid, group) {
  return CACHE_PREFIX + uuid + '.' + group;
}

export function readCache(uuid, group) {
  try {
    const raw = localStorage.getItem(cacheKey(uuid, group));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.t || !obj.v) return null;
    if (Date.now() - obj.t >= CACHE_TTL) { localStorage.removeItem(cacheKey(uuid, group)); return null; }
    return obj.v;
  } catch {
    return null;
  }
}

export function writeCache(uuid, group, data) {
  try {
    const s = JSON.stringify({ t: Date.now(), v: data });
    if (s.length > 1500000) return;
    localStorage.setItem(cacheKey(uuid, group), s);
  } catch {
    /* quota / private mode */
  }
}

export function pruneCache() {
  try {
    const now = Date.now();
    Object.keys(localStorage).forEach(k => {
      if (k.indexOf(CACHE_PREFIX) !== 0) return;
      try {
        const obj = JSON.parse(localStorage.getItem(k));
        if (!obj || !obj.t || now - obj.t >= CACHE_TTL) localStorage.removeItem(k);
      } catch {
        localStorage.removeItem(k);
      }
    });
  } catch {
    /* private mode */
  }
}
